<script setup lang="ts">
import {
  BarChartOutlined,
  BookOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  FileDoneOutlined,
  FormOutlined,
  HeartOutlined,
  HistoryOutlined,
  HomeOutlined,
  ExceptionOutlined,
  MenuOutlined,
  ProfileOutlined,
  SettingOutlined,
} from '@ant-design/icons-vue'
import { computed, ref, type Component } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import AppState from '@/components/common/AppState.vue'
import { useAppStore } from '@/stores/appStore.js'
import { useThemeStore } from '@/stores/themeStore.js'

interface NavigationItem {
  path: string
  label: string
  icon: Component
}

interface NavigationGroup {
  label: string
  items: NavigationItem[]
}

const navigationGroups: NavigationGroup[] = [
  {
    label: '学习',
    items: [
      { path: '/', label: '首页', icon: HomeOutlined },
      { path: '/study', label: '今日学习', icon: FileDoneOutlined },
      { path: '/review', label: '今日复习', icon: ClockCircleOutlined },
    ],
  },
  {
    label: '知识',
    items: [
      { path: '/knowledge', label: '知识库', icon: BookOutlined },
      { path: '/distinctions', label: '易错辨析', icon: ExceptionOutlined },
      { path: '/timeline', label: '常识时间线', icon: HistoryOutlined },
      { path: '/favorites', label: '收藏', icon: HeartOutlined },
    ],
  },
  {
    label: '练习',
    items: [
      { path: '/quiz', label: '开始练习', icon: FormOutlined },
      { path: '/wrong-questions', label: '错题本', icon: CloseOutlined },
      { path: '/practice-history', label: '刷题记录', icon: ProfileOutlined },
      { path: '/practice-attempt-history', label: '练习历史', icon: HistoryOutlined },
    ],
  },
  {
    label: '数据',
    items: [
      { path: '/statistics', label: '学习统计', icon: BarChartOutlined },
      { path: '/settings', label: '设置', icon: SettingOutlined },
    ],
  },
]

const mobileNavigation: NavigationItem[] = [
  { path: '/', label: '首页', icon: HomeOutlined },
  { path: '/knowledge', label: '知识', icon: BookOutlined },
  { path: '/study', label: '学习', icon: FileDoneOutlined },
  { path: '/quiz', label: '练习', icon: FormOutlined },
  { path: '/settings', label: '我的', icon: ProfileOutlined },
]
const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const themeStore = useThemeStore()
const drawerOpen = ref(false)
const selectedKeys = computed(() => [
  route.path.startsWith('/knowledge/')
    ? '/knowledge'
    : route.path.startsWith('/practice-attempt-history/')
      ? '/practice-attempt-history'
      : route.path.startsWith('/practice-history/')
        ? '/practice-history'
        : route.path,
])
const mobileSelectedPath = computed(() => {
  if (
    ['/knowledge', '/distinctions', '/timeline', '/favorites'].some(
      (path) => route.path === path || route.path.startsWith(`${path}/`),
    )
  ) {
    return '/knowledge'
  }
  if (
    ['/quiz', '/wrong-questions', '/practice-history', '/practice-attempt-history'].some(
      (path) => route.path === path || route.path.startsWith(`${path}/`),
    )
  ) {
    return '/quiz'
  }
  if (route.path === '/review') return '/study'
  if (route.path === '/statistics') return '/settings'
  return route.path
})
const pageTitle = computed(() => route.meta.title)

function navigate(path: string): void {
  drawerOpen.value = false
  void router.push(path)
}

function handleMenuClick(event: { key: string | number }): void {
  navigate(String(event.key))
}

function toggleTheme(): void {
  themeStore.setMode(themeStore.resolved === 'dark' ? 'light' : 'dark')
}
</script>

<template>
  <a-layout class="app-shell">
    <a-layout-sider class="desktop-sider" :width="248" theme="light">
      <div class="brand-block" @click="navigate('/')">
        <span class="brand-mark" aria-hidden="true"><CheckSquareOutlined /></span>
        <span>
          <strong>常识研习</strong>
          <small>知识积累与复习</small>
        </span>
      </div>
      <nav aria-label="主导航">
        <a-menu mode="inline" :selected-keys="selectedKeys" @click="handleMenuClick">
          <a-menu-item-group
            v-for="group in navigationGroups"
            :key="group.label"
            :title="group.label"
          >
            <a-menu-item v-for="item in group.items" :key="item.path">
              <template #icon><component :is="item.icon" /></template>
              {{ item.label }}
            </a-menu-item>
          </a-menu-item-group>
        </a-menu>
      </nav>
      <div class="sider-status">
        <span class="status-dot" aria-hidden="true" />
        <span>内容与本地存储已连接</span>
      </div>
    </a-layout-sider>

    <a-layout class="main-layout">
      <a-layout-header class="app-header">
        <a-button
          class="mobile-menu-button"
          type="text"
          aria-label="打开导航菜单"
          @click="drawerOpen = true"
        >
          <MenuOutlined />
        </a-button>
        <div class="header-title">
          <span class="mobile-brand">常识研习</span>
          <strong>{{ pageTitle }}</strong>
        </div>
        <a-button type="text" aria-label="切换明暗主题" @click="toggleTheme">
          <span aria-hidden="true">{{ themeStore.resolved === 'dark' ? '☀' : '☾' }}</span>
        </a-button>
      </a-layout-header>

      <a-layout-content class="app-content">
        <AppState :loading="appStore.loading" :error="appStore.error" @retry="appStore.initialize">
          <RouterView v-slot="{ Component: PageComponent }">
            <Transition name="page-fade" mode="out-in">
              <component :is="PageComponent" :key="route.path" />
            </Transition>
          </RouterView>
        </AppState>
      </a-layout-content>
    </a-layout>

    <a-drawer v-model:open="drawerOpen" title="导航" placement="left" :width="296">
      <a-menu mode="inline" :selected-keys="selectedKeys" @click="handleMenuClick">
        <a-menu-item-group
          v-for="group in navigationGroups"
          :key="group.label"
          :title="group.label"
        >
          <a-menu-item v-for="item in group.items" :key="item.path">
            <template #icon><component :is="item.icon" /></template>
            {{ item.label }}
          </a-menu-item>
        </a-menu-item-group>
      </a-menu>
    </a-drawer>

    <nav class="mobile-bottom-nav" aria-label="移动端主导航">
      <button
        v-for="item in mobileNavigation"
        :key="item.path"
        type="button"
        :class="{ active: mobileSelectedPath === item.path }"
        :aria-current="mobileSelectedPath === item.path ? 'page' : false"
        @click="navigate(item.path)"
      >
        <component :is="item.icon" aria-hidden="true" />
        <span>{{ item.label }}</span>
      </button>
    </nav>
  </a-layout>
</template>
